import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { PageHero } from "@/components/ui/PageHero";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { readyFeaturePages } from "@/marketing/featurePages";
import { useSeo } from "@/lib/seo";

/**
 * ``/features`` — the way into „Co umíme“.
 *
 * The analysis this page answers (2026-09-18) found the public site
 * selling instant quoting to the depth of an entire website and the
 * rest of the product to the depth of a footnote: the landing's line
 * "a další — sklad, šarže, štítky" was, quite literally, the whole
 * pitch for half the administration. The pillar the owner named on
 * 2026-09-06 — modularity — had had no surface at all since KAN-906/907
 * were parked.
 *
 * So the H1 says the quiet part: quoting is not the product, it is the
 * first step of it. The tiles are the rest of the step.
 *
 * The first tile is quoting, with a page of its own since the
 * 2026-10-07 audit (#61). It used to go to the landing, which left the
 * core of the product as the one tile with nothing behind it.
 *
 * Tiles come from ``FEATURE_PAGES`` and only the ones with a route
 * render; see that module for why the not-yet-shipped pages are absent
 * rather than greyed out.
 */
export default function FeaturesHub(): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  useSeo({
    title: t("features.hub.seo.title"),
    description: t("features.hub.seo.description"),
    path: "/features",
    lang,
  });

  const tiles = readyFeaturePages();

  return (
    <div data-testid="features-hub">
      <PageHero innerClassName="max-w-4xl px-4 py-16 sm:py-20">
        <p className="text-sm font-medium uppercase tracking-wide text-ink-3">
          {t("features.hub.eyebrow")}
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
          {t("features.hub.title")}
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-ink-2">
          {t("features.hub.lead")}
        </p>
      </PageHero>

      <section className="mx-auto max-w-4xl px-4 py-12">
        <ul className="grid gap-4 sm:grid-cols-2">
          {tiles.map((tile) => (
            <li key={tile.testId}>
              <Link
                to={`/${lang}${tile.to}`}
                className="block h-full rounded-lg border border-border p-5 transition-colors hover:bg-surface-2"
                data-testid={tile.testId}
              >
                <span className="font-medium text-ink">
                  {t(`features.tiles.${tile.i18nKey}.title`)}
                </span>
                <span className="mt-1 block text-sm text-ink-2">
                  {t(`features.tiles.${tile.i18nKey}.lead`)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-8 max-w-2xl text-sm text-ink-2">
          {t("features.hub.modularity")}
        </p>
      </section>
    </div>
  );
}
